import { Checkbox, Flex, NumberInput, Title } from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import { theme } from "../../theme";

const daysOfWeek = [
  "Pazar",
  "Pazartesi",
  "Salı",
  "Çarşamba",
  "Perşembe",
  "Cuma",
  "Cumartesi",
];

const timeStrToDate = (timeStr: any) => {
  if (!timeStr) return null;
  const [hour, minute] = timeStr.split(":").map(Number);
  if (isNaN(hour) || isNaN(minute)) return null;
  return dayjs().hour(hour).minute(minute).second(0).toDate();
};

const dateToTimeStr = (date: any) => {
  return date ? dayjs(date).format("HH:mm") : "";
};

type WeeklyScheduleType = {
  enabled: boolean;
  start: string;
  duration: number;
  end: string;
}[];

type Props = {
  value: WeeklyScheduleType;
  onChange: (value: WeeklyScheduleType) => void;
  error: any;
  direction?: any;
};

export default function WeeklySchedule({
  value,
  onChange,
  error,
  direction,
}: Props) {
  const defaultWorkHours: WeeklyScheduleType = Array(7)
    .fill(null)
    .map(() => ({
      enabled: false,
      start: "",
      duration: 0,
      end: "",
    }));

  const workHours = value ?? defaultWorkHours;

  const handleCheckboxChange = (index: number, checked: boolean) => {
    const updated = [...workHours];
    updated[index] = {
      enabled: checked,
      start: checked ? updated[index].start : "",
      duration: checked ? updated[index].duration : 0,
      end: "",
    };
    onChange(updated);
  };

  const handleStartTimeChange = (index: number, timeStr: string) => {
    const updated = [...workHours];
    updated[index].start = timeStr;

    const startDate = timeStrToDate(timeStr);
    if (startDate && updated[index].duration) {
      const endDate = dayjs(startDate)
        .add(updated[index].duration, "hour")
        .toDate();
      updated[index].end = dateToTimeStr(endDate);
    } else {
      updated[index].end = "";
    }

    onChange(updated);
  };

  const handleDurationChange = (index: number, duration: number) => {
    const updated = [...workHours];
    updated[index].duration = duration;

    const startDate = timeStrToDate(updated[index].start);
    if (startDate && duration) {
      const endDate = dayjs(startDate).add(duration, "hour").toDate();
      updated[index].end = dateToTimeStr(endDate);
    } else {
      updated[index].end = "";
    }

    onChange(updated);
  };

  const displayDays = [...daysOfWeek.slice(1), daysOfWeek[0]];

  return (
    <Flex gap={12} direction="column" w="100%">
      <Flex gap={24}>
        <div
          style={{
            fontWeight: "500",
            fontSize: "16px",
          }}
        >
          Haftalık Mesai Takvimi
        </div>
        {error && (
          <div
            style={{
              color: theme.colors.alert.red,
            }}
          >
            ( {error}! )
          </div>
        )}
      </Flex>
      <Flex gap={12} flex={1} direction={direction}>
        {displayDays.map((day, displayIndex) => {
          const originalIndex = daysOfWeek.indexOf(day);

          return (
            <Flex
              key={day}
              flex={1}
              gap={12}
              direction="column"
              h={"min-content"}
              p="sm"
              style={{ border: "1px solid #ddd", borderRadius: 8 }}
            >
              <Checkbox
                color="#5C26FF"
                label={day}
                checked={workHours[originalIndex].enabled}
                onChange={(e) =>
                  handleCheckboxChange(originalIndex, e.currentTarget.checked)
                }
              />
              {workHours[originalIndex].enabled && (
                <Flex direction="column" gap="xs">
                  <TimeInput
                    label="Başlangıç Saati"
                    value={workHours[originalIndex].start}
                    onChange={(e) =>
                      handleStartTimeChange(
                        originalIndex,
                        e.currentTarget.value
                      )
                    }
                  />
                  <NumberInput
                    label="Çalışma Süresi (saat)"
                    value={workHours[originalIndex].duration}
                    onChange={(value) =>
                      handleDurationChange(
                        originalIndex,
                        typeof value === "number" ? value : 0
                      )
                    }
                    min={0}
                  />
                  <TimeInput
                    label="Bitiş Saati"
                    value={workHours[originalIndex].end}
                    disabled
                  />
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
